import React, { useCallback, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { WarningIconBlack } from "@/assets/images";
import { showSnackbar } from "@store/slices/snackbar-slice";
import { useAppDispatch } from "@store/index";
import UserRow from "../features/dashboard-grid/UserRow";
import HPButton from "../common/Button";
import InviteNewAccount from "../features/share-modal-tabs/InviteNewAccount";
import ShareExistingUsers from "../features/share-modal-tabs/ShareExistingUsers";
import { useShareModalLogic, User } from "@/hooks/use-share-modal-logic";

interface ShareDashboardModalProps {
  dashboardName: string;
  closeAction: () => void;
  dashboardId: string;
  onAccessChanged?: () => void;
}

const RevocationConfirmation: React.FC<{
  user: User;
  onConfirm: (email: string) => void;
  onCancel: () => void;
}> = ({ user, onConfirm, onCancel }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      width: "100%",
      ml: -1,
    }}
  >
    <Box
      sx={{
        width: "35px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    ></Box>

    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }} flex={1}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WarningIconBlack />
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            fontSize: "14px",
          }}
        >
          You are about to revoke the access from this account. Continue?
        </Typography>
      </Box>
    </Box>

    <Box sx={{ display: "flex", gap: 1 }}>
      <HPButton
        variant="text"
        onClick={() => onConfirm(user.email)}
        className="px-[0px] font-bold"
        color="error"
      >
        Revoke
      </HPButton>
      <HPButton
        variant="text"
        onClick={onCancel}
        className="px-[0px] font-bold"
      >
        Cancel
      </HPButton>
    </Box>
  </Box>
);

const ShareDashboardModal: React.FC<ShareDashboardModalProps> = ({
  dashboardName,
  closeAction,
  dashboardId,
  onAccessChanged,
}) => {
  const dispatch = useAppDispatch();

  const handleShowSnackbar = useCallback(
    (message: string, severity: "error" | "success") => {
      dispatch(showSnackbar({ message, severity }));
    },
    [dispatch],
  );

  const {
    selectedUsers,
    searchTerm,
    selectedClientId,
    activeTab,
    newAccounts,
    revocationTarget,
    validationErrors,
    isSearchingUsers,
    hasInitiallyLoaded,

    sharedUsers,
    nonSharedUsers,
    allExistingUsers,

    setSearchTerm,
    handleSelectUser,
    handleClientFilterChange,
    handleTabChange,
    handleRevokeClick,
    confirmRevoke,
    cancelRevoke,
    handleAddAccount,
    handleRemoveAccount,
    handleAccountChange,
    handleInvite,
    handleShare,
    shareAllPublishedDashboards,
    setShareAllPublishedDashboards,
  } = useShareModalLogic(
    dashboardId,
    closeAction,
    handleShowSnackbar,
    onAccessChanged,
  );

  const renderUserList = (list: User[], isSharedList: boolean) => (
    <Box>
      {list.map((user) => {
        const isRevokeActive = isSharedList && revocationTarget === user.email;

        return (
          <Box
            key={user.email}
            sx={{
              py: isRevokeActive ? 0.5 : 0,
            }}
          >
            {isRevokeActive ? (
              <RevocationConfirmation
                user={user}
                onConfirm={confirmRevoke}
                onCancel={cancelRevoke}
              />
            ) : (
              <UserRow
                user={user}
                onSelect={handleSelectUser}
                isSelected={selectedUsers.includes(user.email)}
                onRevoke={isSharedList ? handleRevokeClick : undefined}
                isShared={isSharedList}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Dialog open={true} onClose={closeAction} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Share {dashboardName}</Typography>
          <IconButton onClick={closeAction}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{ paddingTop: "20px !important", overflowY: "auto" }}
        dividers
      >
        <Box>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": { textTransform: "none" },
            }}
          >
            <Tab
              className="normal-case p-0 w-fit text-left min-w-2 mr-10"
              label="Share with existing"
            />
            <Tab
              className="normal-case p-0 w-fit text-left min-w-2 mr-10"
              label="Invite new account"
            />
          </Tabs>
        </Box>
        {activeTab === 0 && (
          <ShareExistingUsers
            sharedUsers={sharedUsers}
            nonSharedUsers={nonSharedUsers}
            allExistingUsers={allExistingUsers}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedClientId={selectedClientId}
            handleClientFilterChange={handleClientFilterChange}
            selectedUsers={selectedUsers}
            handleSelectUser={handleSelectUser}
            revocationTarget={revocationTarget}
            confirmRevoke={confirmRevoke}
            cancelRevoke={cancelRevoke}
            handleRevokeClick={handleRevokeClick}
            renderUserList={renderUserList}
            dashboardId={dashboardId}
            isSearchingUsers={isSearchingUsers}
            hasInitiallyLoaded={hasInitiallyLoaded}
          />
        )}
        {activeTab === 1 && (
          <InviteNewAccount
            newAccounts={newAccounts}
            handleAccountChange={handleAccountChange}
            handleAddAccount={handleAddAccount}
            handleRemoveAccount={handleRemoveAccount}
            validationErrors={validationErrors}
            dashboardId={dashboardId}
          />
        )}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "space-between",
          padding: "16px 24px",
          borderTop: "1px solid #D9D9D9",
        }}
      >
        {activeTab === 0 && (
          <HPButton
            variant="text"
            onClick={closeAction}
            className="text-inherit"
          >
            Done
          </HPButton>
        )}

        {activeTab === 0 ? (
          <Box display="flex" alignItems="center" gap={2}>
            <HPButton
              variant="contained"
              disabled={selectedUsers.length === 0 || !!revocationTarget}
              onClick={handleShare}
            >
              Share
            </HPButton>
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="flex-end"
            gap={2}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={shareAllPublishedDashboards}
                  onChange={(e) =>
                    setShareAllPublishedDashboards(e.target.checked)
                  }
                />
              }
              label={
                <Typography variant="body2" className="text-sm">
                  Assign Viewer role on all other Published dashboards
                </Typography>
              }
            />
            <HPButton variant="contained" onClick={handleInvite}>
              Invite
            </HPButton>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareDashboardModal;
